import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Track } from '../types/music';
import { readStore, writeStore } from '../utils/storage';
import { searchTracks } from '../utils/itunes';
import { driftTerms } from '../data/seeds';
const SLEEP_FADE_MS = 20000;
interface PlayerValue {
  currentTrack: Track | null;
  queue: Track[];
  queueIndex: number;
  isPlaying: boolean;
  isLoading: boolean;
  progress: number;
  duration: number;
  volume: number;
  muted: boolean;
  /** Night Drift: keeps the queue alive by pulling related records as it empties. */
  drift: boolean;
  isDrifting: boolean;
  sleepSecondsLeft: number | null;
  /** 0..1 amplitude read from the live AnalyserNode (falls back to a gentle idle pulse). */
  getLevel: () => number;
  /** Normalised frequency bins (0..1) for waveform drawing; empty without an analyser. */
  getSpectrum: () => Float32Array;
  play: (tracks: Track[], startIndex?: number) => void;
  toggle: () => void;
  next: () => void;
  previous: () => void;
  seek: (seconds: number) => void;
  setVolume: (value: number) => void;
  toggleMute: () => void;
  setDrift: (value: boolean) => void;
  setSleepMinutes: (minutes: number | null) => void;
}
const PlayerContext = createContext<PlayerValue | null>(null);
export function PlayerProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [audio] = useState(() => {
    const el = new Audio();
    el.crossOrigin = 'anonymous';
    el.preload = 'metadata';
    return el;
  });
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const binsRef = useRef<Uint8Array | null>(null);
  const spectrumRef = useRef<Float32Array>(new Float32Array(0));
  const corsAllowedRef = useRef(true);
  const loadedUrlRef = useRef<string | null>(null);
  const fadeRef = useRef(1);
  const extendingRef = useRef(false);
  const [queue, setQueue] = useState<Track[]>([]);
  const [index, setIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(30);
  const [volume, setVolumeState] = useState(() => readStore<number>('volume', 0.8));
  const [muted, setMuted] = useState(() => readStore<boolean>('muted', false));
  const [drift, setDriftState] = useState(() => readStore<boolean>('drift', false));
  const [isDrifting, setIsDrifting] = useState(false);
  const [sleepEndsAt, setSleepEndsAt] = useState<number | null>(null);
  const [sleepSecondsLeft, setSleepSecondsLeft] = useState<number | null>(null);
  const currentTrack = index >= 0 && index < queue.length ? queue[index] : null;

  // ---- audio element wiring -------------------------------------------------
  useEffect(() => {
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 30);
    const onPlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onEnded = () => setIndex(i => i >= 0 ? i + 1 : i);
    const onError = () => {
      // Some preview hosts reject the CORS preflight. Retry without it and
      // give up on analyser data rather than losing playback entirely.
      if (audio.crossOrigin && loadedUrlRef.current) {
        corsAllowedRef.current = false;
        analyserRef.current = null;
        audio.crossOrigin = null;
        audio.src = loadedUrlRef.current;
        audio.load();
        void audio.play().catch(() => setIsPlaying(false));
        return;
      }
      setIsPlaying(false);
      setIsLoading(false);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.pause();
    };
  }, [audio]);
  useEffect(() => {
    audio.volume = Math.max(0, Math.min(1, volume * fadeRef.current));
    audio.muted = muted;
    writeStore('volume', volume);
    writeStore('muted', muted);
  }, [audio, volume, muted]);
  useEffect(() => writeStore('drift', drift), [drift]);
  const ensureAnalyser = useCallback(() => {
    if (!corsAllowedRef.current) return;
    if (audioCtxRef.current) {
      void audioCtxRef.current.resume();
      return;
    }
    try {
      const Ctor = window.AudioContext ?? (window as unknown as {
        webkitAudioContext: typeof AudioContext;
      }).webkitAudioContext;
      const ctx = new Ctor();
      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.82;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      binsRef.current = new Uint8Array(analyser.frequencyBinCount);
    } catch {
      analyserRef.current = null;
    }
  }, [audio]);

  // ---- Night Drift: extend the queue with related records ------------------
  const extendQueue = useCallback(async () => {
    if (extendingRef.current) return;
    const seed = queue[queue.length - 1];
    if (!seed) return;
    extendingRef.current = true;
    setIsDrifting(true);
    const terms = [seed.genre, seed.artist, driftTerms[Math.floor(Math.random() * driftTerms.length)]].filter(Boolean) as string[];
    const known = new Set(queue.map(t => t.id));
    try {
      for (const term of terms) {
        const found = await searchTracks(term, 20);
        const fresh = found.filter(t => !known.has(t.id));
        if (fresh.length) {
          const picks = fresh.sort(() => Math.random() - 0.5).slice(0, 6);
          setQueue(current => [...current, ...picks]);
          return;
        }
      }
      setIndex(-1);
    } catch {
      setIndex(-1);
    } finally {
      extendingRef.current = false;
      setIsDrifting(false);
    }
  }, [queue]);

  // ---- load + play whenever the pointer into the queue changes -------------
  useEffect(() => {
    if (index < 0) return;
    if (index >= queue.length) {
      if (drift && queue.length) {
        void extendQueue();
        return;
      }
      setIndex(-1);
      setIsPlaying(false);
      return;
    }
    const track = queue[index];
    if (loadedUrlRef.current !== track.previewUrl) {
      loadedUrlRef.current = track.previewUrl;
      audio.src = track.previewUrl;
      setProgress(0);
      setDuration(30);
      setIsLoading(true);
    }
    ensureAnalyser();
    void audio.play().catch(() => setIsPlaying(false));
  }, [audio, drift, ensureAnalyser, extendQueue, index, queue]);

  // ---- sleep fade ----------------------------------------------------------
  useEffect(() => {
    if (sleepEndsAt === null) {
      fadeRef.current = 1;
      audio.volume = Math.max(0, Math.min(1, volume));
      setSleepSecondsLeft(null);
      return;
    }
    const tick = () => {
      const remaining = sleepEndsAt - Date.now();
      setSleepSecondsLeft(Math.max(0, Math.ceil(remaining / 1000)));
      fadeRef.current = remaining <= 0 ? 0 : Math.min(1, remaining / SLEEP_FADE_MS);
      audio.volume = Math.max(0, Math.min(1, volume * fadeRef.current));
      if (remaining <= 0) {
        audio.pause();
        fadeRef.current = 1;
        audio.volume = Math.max(0, Math.min(1, volume));
        setSleepEndsAt(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [audio, sleepEndsAt, volume]);
  const play = useCallback((tracks: Track[], startIndex = 0) => {
    if (!tracks.length) return;
    const target = tracks[startIndex];
    if (target && loadedUrlRef.current === target.previewUrl) {
      setQueue(tracks);
      setIndex(startIndex);
      ensureAnalyser();
      if (audio.paused) void audio.play().catch(() => setIsPlaying(false));else audio.pause();
      return;
    }
    setQueue(tracks);
    setIndex(startIndex);
  }, [audio, ensureAnalyser]);
  const toggle = useCallback(() => {
    if (index < 0) {
      if (queue.length) setIndex(0);
      return;
    }
    ensureAnalyser();
    if (audio.paused) void audio.play().catch(() => setIsPlaying(false));else audio.pause();
  }, [audio, ensureAnalyser, index, queue.length]);
  const next = useCallback(() => {
    setIndex(i => i < 0 ? i : i + 1);
  }, []);
  const previous = useCallback(() => {
    if (audio.currentTime > 4) {
      audio.currentTime = 0;
      return;
    }
    setIndex(i => i > 0 ? i - 1 : i);
  }, [audio]);
  const seek = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds)) return;
    audio.currentTime = Math.max(0, Math.min(seconds, audio.duration || seconds));
    setProgress(audio.currentTime);
  }, [audio]);
  const setVolume = useCallback((value: number) => {
    setVolumeState(Math.max(0, Math.min(1, value)));
    if (value > 0) setMuted(false);
  }, []);
  const toggleMute = useCallback(() => setMuted(m => !m), []);
  const setDrift = useCallback((value: boolean) => setDriftState(value), []);
  const setSleepMinutes = useCallback((minutes: number | null) => {
    setSleepEndsAt(minutes === null ? null : Date.now() + minutes * 60_000);
  }, []);
  const getLevel = useCallback(() => {
    const analyser = analyserRef.current;
    const bins = binsRef.current;
    if (analyser && bins) {
      analyser.getByteFrequencyData(bins);
      let sum = 0;
      for (let i = 0; i < bins.length; i += 1) sum += bins[i];
      const avg = sum / bins.length / 255;
      if (avg > 0.001) return avg;
    }
    if (!isPlaying) return 0;
    // Analyser unavailable (CORS-blocked preview): keep the scene alive subtly.
    return 0.3 + Math.sin(performance.now() / 260) * 0.08;
  }, [isPlaying]);
  const getSpectrum = useCallback(() => {
    const analyser = analyserRef.current;
    const bins = binsRef.current;
    if (!analyser || !bins) return spectrumRef.current;
    if (spectrumRef.current.length !== bins.length) spectrumRef.current = new Float32Array(bins.length);
    analyser.getByteFrequencyData(bins);
    const out = spectrumRef.current;
    for (let i = 0; i < bins.length; i += 1) out[i] = bins[i] / 255;
    return out;
  }, []);
  const value: PlayerValue = {
    currentTrack,
    queue,
    queueIndex: index,
    isPlaying,
    isLoading,
    progress,
    duration,
    volume,
    muted,
    drift,
    isDrifting,
    sleepSecondsLeft,
    getLevel,
    getSpectrum,
    play,
    toggle,
    next,
    previous,
    seek,
    setVolume,
    toggleMute,
    setDrift,
    setSleepMinutes
  };
  return <PlayerContext.Provider value={value} data-id="element-263" data-mp="1xhtogz271agu">{children}</PlayerContext.Provider>;
}
export function usePlayer(): PlayerValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used inside a PlayerProvider');
  return ctx;
}